import type { GetDashboardInitialData } from 'lib/queries/dashboard';
import type { NavbarProps } from './navbar';

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useResizeObserver, useUniqueId, Wrap } from '@leafwell/components';

import DasboardProvider, {
  useDasboardContext,
} from 'contexts/dashboard-context';
import { useWordPressContext } from 'contexts/wordpress-provider';

import DashboardConditions from './dashboard-conditions';
import DashboardForm from './dashboard-form';
import DashboardHeader from './dashboard-header';
import DashboardOrganSystems from './dashboard-organ-systems';
import DashboardStudies from './dashboard-studies';
import Loader from './loader';
import DashboardStudiesYear from './dashboard-studies-year';
import DashboardLocations from './dashboard-locations';
import DashboardCannabinoids from './dashboard-cannabinoids';
import Navbar from './navbar';
import CardEmpty from './card-empty';
import Figure from './figure';
import { useDraggableScroll } from 'hooks/useDraggableScroll';
import { initializeWpApollo } from 'lib/apollo';
import { ApolloProvider } from '@apollo/client';

export type DashboardSections = { sections: NavbarProps['items'] };

const DashboardNavbar: React.FC<DashboardSections> = ({ sections }) => {
  const wrapperEl = useRef<HTMLDivElement>(null);
  const navbarEl = useRef<HTMLDivElement>(null);
  const [elHeight, setElHeight] = useState(0);
  const [isFormOpen, toggleFormVisibility] = useState(false);
  const { onMouseDown, updateScroll } = useDraggableScroll({
    ref: navbarEl,
    direction: 'horizontal',
  });

  const observable = useMemo(() => {
    if (!sections || typeof document === 'undefined') {
      return sections;
    }

    return sections
      ?.map(({ id, ...item }) => {
        const anchor = document.getElementById(id);
        const firstChild = anchor?.firstElementChild;

        if (firstChild || anchor) {
          return {
            ...item,
            id,
            node: firstChild || anchor,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [sections, typeof document]);

  useResizeObserver(entry => {
    const currHeight = entry?.target?.clientHeight;

    if (currHeight) {
      setElHeight(Math.ceil(currHeight + 60)); // offset dashboard grid gap

      document.documentElement.style.setProperty(
        '--dashboard-form-height',
        `${Math.ceil(currHeight)}px`,
      );
    }
  }, wrapperEl);

  return (
    <section
      ref={wrapperEl}
      className={[
        'sticky top-0 bg-gray-100 pb-2 lg:pb-0 lg:pt-1',
        isFormOpen ? 'z-30' : 'z-10',
      ].join(' ')}
    >
      <DashboardForm onToggleForm={open => toggleFormVisibility(open)} />
      <Navbar
        {...{
          classNameWrap: 'cursor-grab flex overflow-x-auto hide-scrollbar',
          className:
            'w-full justify-between flex-nowrap whitespace-nowrap gap-x-6',
          innerNav: true,
          items: observable,
          offset: elHeight,
          onMouseDown,
          onUpdate: ({ node }) => updateScroll(node.offsetLeft - 30),
          ref: navbarEl,
          size: 'default',
        }}
      />
    </section>
  );
};

const DashboardContent: React.FC<DashboardSections> = memo(({ sections }) => {
  const sectionId = useUniqueId('dashboard');
  const { conditionTypesImage } = useWordPressContext();
  const {
    loading,
    studies: { total },
  } = useDasboardContext();
  const getSectionId = useCallback(
    (index: number) => sections?.[index]?.id || '',
    [sections],
  );

  useEffect(() => {
    if (loading) {
      document
        .getElementById(sectionId)
        ?.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }, [sectionId, loading]);

  if (loading) {
    return (
      <Wrap id={sectionId} className="h-3/4-screen-height">
        <Loader fullHeight={false} />
      </Wrap>
    );
  }

  if (total <= 0) {
    return (
      <Wrap className="md:grid-flow-col items-center" size="small">
        {conditionTypesImage && (
          <Figure
            {...{
              ...conditionTypesImage,
              className: 'hidden md:block',
            }}
          />
        )}
        <CardEmpty className="md:border-0" />
      </Wrap>
    );
  }

  return (
    <Wrap size="large">
      <DashboardHeader id={getSectionId(0)} />
      <DashboardStudiesYear id={getSectionId(1)} />
      <DashboardOrganSystems id={getSectionId(2)} />
      <DashboardConditions id={getSectionId(3)} />
      <DashboardLocations id={getSectionId(4)} />
      <DashboardCannabinoids id={getSectionId(5)} />
      <DashboardStudies id={getSectionId(6)} />
    </Wrap>
  );
});

const Dashboard: React.FC<{
  initialData: GetDashboardInitialData;
}> = ({ initialData }) => {
  const { translations } = useWordPressContext();
  const sections = useMemo(
    () =>
      [
        { name: translations['overview'], id: 'overview' },
        { name: translations['Timeline'], id: 'year' },
        { name: translations['Organ Systems'], id: 'system' },
        { name: translations['Medical Conditions'], id: 'conditions' },
        { name: translations['Countries'], id: 'country' },
        { name: translations['Cannabinoids'], id: 'cannabinoids' },
        { name: translations['Studies List'], id: 'studies' },
      ].map(item => ({ ...item, uri: `#${item.id}` })),
    [translations],
  );
  const apolloClient = initializeWpApollo();

  return (
    <ApolloProvider client={apolloClient}>
      <DasboardProvider initialValues={initialData}>
        <DashboardNavbar {...{ sections }} />
        <section className="relative before:absolute before:-z-1 before:w-screen before:h-1/2-screen-height before:bg-gradient-to-r before:from-lilac-100 before:to-green-200 before:-top-10 before:lg:-top-12 before:left-1/2 before:-translate-x-1/2 before:gradient-mask-b-0">
          <DashboardContent {...{ sections }} />
        </section>
      </DasboardProvider>
    </ApolloProvider>
  );
};

export default Dashboard;
